<template>
  <TabPanel class="tw-w-full tw-space-y-8">
    <div class="tw-grid tw-grid-cols-12 tw-gap-5">
      <Box rounded="xlarge" class="tw-col-span-full tw-space-y-5 tw-bg-white/5 md:tw-col-span-6">
        <div class="tw-space-y-5">
          <Heading level="h4">DEV</Heading>

          <div class="tw-space-y-3">
            <Checkbox v-model="form.use_dev_registry" :right-label="t('development.download_from_dev_registry')" />
          </div>

          <div class="tw-grid tw-grid-cols-12 tw-items-end tw-gap-3">
            <div class="tw-col-span-8 md:tw-col-span-10">
              <FormField
                readonly="true"
                :label="t('development.downloaded_version')"
                :placeholder="devDownloadedVersion"
              />
            </div>

            <div class="tw-col-span-4 md:tw-col-span-2">
              <Button
                color="semiTransparentWhite"
                class="tw-w-full md:tw-w-auto"
                :disabled="!devDownloadedImage"
                :loading="runningKey === 'devRemote'"
                @click="runDevRemote"
              >
                {{ t('development.run') }}
              </Button>
            </div>
          </div>
        </div>

        <div class="tw-space-y-5">
          <Heading level="h4">{{ t('development.custom_version') }}</Heading>

          <div class="tw-grid tw-grid-cols-12 tw-items-end tw-gap-3">
            <div class="tw-col-span-8 md:tw-col-span-10">
              <FormField v-model="form.custom_version" label="Tag" placeholder="git-xxxxxxx" />
            </div>

            <div class="tw-col-span-4 md:tw-col-span-2">
              <Button
                color="semiTransparentWhite"
                class="tw-w-full md:tw-w-auto"
                :disabled="!canRunCustom"
                :loading="runningKey === 'custom'"
                @click="runCustom"
              >
                {{ t('development.run') }}
              </Button>
            </div>
          </div>
        </div>

        <div class="tw-space-y-5">
          <Heading level="h4">{{ t('development.local_version') }}</Heading>

          <div class="tw-grid tw-grid-cols-12 tw-items-end tw-gap-3">
            <div class="tw-col-span-8 md:tw-col-span-10">
              <FormField v-model="form.local_version" type="select" label="Version">
                <option value="" disabled>Select a local version</option>
                <option v-for="img in devLocalImages" :key="img.tag" :value="img.tag">
                  {{ img.label }}
                </option>
              </FormField>
            </div>

            <div class="tw-col-span-4 md:tw-col-span-2">
              <Button
                color="semiTransparentWhite"
                class="tw-w-full md:tw-w-auto"
                :disabled="!form.local_version"
                :loading="runningKey === 'local'"
                @click="runLocal"
              >
                {{ t('development.run') }}
              </Button>
            </div>
          </div>

          <div class="tw-flex tw-justify-end">
            <Button color="semiTransparentWhite" class="tw-w-fit" @click="refreshImages">
              {{ t('development.refresh_images') }}
            </Button>
          </div>
        </div>
      </Box>

      <Box rounded="xlarge" class="tw-col-span-full tw-space-y-5 tw-bg-white/5 md:tw-col-span-6">
        <Heading level="h4">DEV wiring</Heading>

        <template v-for="[key, field] in devFieldsList" :key="key">
          <Checkbox
            v-if="field.type === 'checkbox'"
            v-model="modelsByKey[key]"
            :right-label="field.label"
            :message="field.description"
            :message-attrs="{ class: 'tw-px-0' }"
          />

          <FormField
            v-else-if="field.type === 'text' && key === 'custom_volume_mounting'"
            v-model="mountModel"
            :label="field.label"
            :message="field.description"
            :message-attrs="{ class: 'tw-px-0' }"
            :placeholder="field.placeholder || undefined"
            @focus="onMountFocus"
            @blur="onMountBlur"
          />

          <FormField
            v-else-if="field.type === 'text'"
            v-model="modelsByKey[key]"
            :label="field.label"
            :message="field.description"
            :message-attrs="{ class: 'tw-px-0' }"
            :placeholder="field.placeholder || undefined"
          />
        </template>

        <Button v-if="isChangedDev" color="semiTransparentWhite" class="tw-w-fit" @click="resetDevToDefaults">
          Reset to defaults
        </Button>
      </Box>
    </div>

    <div class="tw-space-y-4">
      <div class="tw-flex tw-flex-wrap tw-items-center tw-space-x-2">
        <Link href="#" scheme="underline" @click.prevent="openLogs('nate')">NATE logs</Link>
        <Link href="#" scheme="underline" @click.prevent="openLogs('runner')">NATE Runner logs</Link>
      </div>
    </div>

    <LogsModal v-model="showLogs" :kind="logsKind" />
  </TabPanel>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { Box, Button, Checkbox, FormField, Heading, Link, TabPanel } from '@slideslive/fuse-kit/vue';

import LogsModal from '@/components/LogsModal.vue';
import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';
import { normalizeWiringValue } from '@/utils/sidebar_status';

const { t } = useI18n();

const scope = 'development';
const stateStore = useStateStore();

const { setWiringDraftValue, resetWiringDraftToDefaults } = stateStore;
const { state, wiringDraftDevelopment, wiringValues } = storeToRefs(stateStore);

const nate = computed(() => state.value?.data?.nate || {});
const nateStatus = computed(() => nate.value?.status?.trim() || '');

const devFields = computed(() => state.value?.data?.wiring?.fields?.[scope] || {});
const devFieldsList = computed(() => Object.entries(devFields.value || {}));

const imagesLocal = computed(() => state.value?.data?.images?.local || []);
const devLocal = computed(() => state.value?.data?.channels?.development?.dev?.local || null);
const devLocalTag = computed(() => String(devLocal.value?.tag || '').trim());

const devDownloadedImage = computed(() => {
  const tag = devLocalTag.value;
  if (!tag) return null;
  return (imagesLocal.value || []).find((img) => String(img?.tag || '').trim() === tag) || null;
});

const devDownloadedVersion = computed(() => {
  const img = devDownloadedImage.value;
  if (!img) return '-';
  return String(img?.version_tag || img?.version || img?.tag || '-');
});

const devLocalImages = computed(() =>
  (imagesLocal.value || [])
    .filter((img) => typeof img?.tag === 'string' && img.tag.startsWith('git-'))
    .map((img) => {
      const tag = String(img.tag || '');
      const version = String(img.version_tag || img.version || tag);
      const createdAt = img.created_at ? String(img.created_at) : '';

      return {
        tag,
        version,
        created_at: createdAt,
        label: createdAt ? `${version} • ${createdAt}` : version,
      };
    }),
);

const form = reactive({
  use_dev_registry: false,
  local_version: '',
  custom_version: '',
});

const hasDraftValue = (key) => Object.prototype.hasOwnProperty.call(wiringDraftDevelopment.value || {}, key);

const modelsByKey = reactive({});

const createModelForKey = (key) =>
  computed({
    get() {
      const field = devFields.value?.[key];
      const raw = wiringDraftDevelopment.value?.[key];
      return normalizeWiringValue(field, raw);
    },
    set(value) {
      const field = devFields.value?.[key];
      setWiringDraftValue(scope, key, normalizeWiringValue(field, value));
    },
  });

const editingMount = ref(false);
const mountBuffer = ref('');

const mountModel = computed({
  get() {
    if (editingMount.value) return mountBuffer.value;
    const field = devFields.value?.custom_volume_mounting;
    const raw = wiringDraftDevelopment.value?.custom_volume_mounting;
    return normalizeWiringValue(field, raw);
  },
  set(value) {
    mountBuffer.value = String(value ?? '');
  },
});

const onMountFocus = () => {
  editingMount.value = true;
  mountBuffer.value = String(wiringDraftDevelopment.value?.custom_volume_mounting ?? '');
};

const onMountBlur = () => {
  editingMount.value = false;
  setWiringDraftValue(scope, 'custom_volume_mounting', mountBuffer.value);
};

watch(
  devFields,
  (fields) => {
    const group = fields || {};

    Object.entries(group).forEach(([key, field]) => {
      if (!hasDraftValue(key)) {
        const initialRaw = field?.value ?? field?.default ?? '';
        setWiringDraftValue(scope, key, normalizeWiringValue(field, initialRaw));
      }

      if (!modelsByKey[key] && key !== 'custom_volume_mounting') {
        modelsByKey[key] = createModelForKey(key);
      }

      if (key === 'custom_volume_mounting' && mountBuffer.value === '' && !editingMount.value) {
        const initial = wiringDraftDevelopment.value?.[key] ?? field?.value ?? field?.default ?? '';
        mountBuffer.value = String(initial ?? '');
      }
    });
  },
  { immediate: true },
);

watch(
  wiringDraftDevelopment,
  (draft) => {
    if (!editingMount.value) {
      mountBuffer.value = String((draft || {}).custom_volume_mounting ?? mountBuffer.value ?? '');
    }
  },
  { immediate: true },
);

const isChangedDev = computed(() => {
  const group = devFields.value || {};
  const draft = wiringDraftDevelopment.value || {};

  return Object.entries(group).some(([key, field]) => {
    const draftVal = normalizeWiringValue(field, draft[key]);
    const defaultVal = normalizeWiringValue(field, field?.default);
    return draftVal !== defaultVal;
  });
});

const resetDevToDefaults = (event) => {
  if (event) event.preventDefault();
  resetWiringDraftToDefaults('development');
};

const canRunCustom = computed(() => String(form.custom_version || '').trim().length > 0);

const runningKey = ref(null);
const sawRestart = ref(false);

watch(nateStatus, (s) => {
  if (!runningKey.value) return;

  if (s !== 'running') sawRestart.value = true;

  if (sawRestart.value && s !== 'restarting') {
    runningKey.value = null;
    sawRestart.value = false;
  }
});

const withLoadingUntilNateReady = async (key, tag, fn) => {
  if (runningKey.value) return;

  runningKey.value = key;

  try {
    await fn();
  } catch (e) {
    runningKey.value = null;
    sawRestart.value = false;
    throw e;
  }
};

const postNateRun = (tag) =>
  fetcher.post('/nate/-/run', {
    tag,
    use_dev_registry: Boolean(form.use_dev_registry),
    wiring_values: wiringValues.value,
  });

const runDevRemote = async () => {
  const tag = devLocalTag.value;
  if (!tag) return;
  await withLoadingUntilNateReady('devRemote', tag, () => postNateRun(tag));
};

const runCustom = async () => {
  const tag = String(form.custom_version || '').trim();
  if (!tag) return;
  await withLoadingUntilNateReady('custom', tag, () => postNateRun(tag));
};

const runLocal = async () => {
  const tag = String(form.local_version || '').trim();
  if (!tag) return;
  await withLoadingUntilNateReady('local', tag, () => postNateRun(tag));
};

const refreshImages = () => fetcher.post('/images/-/refresh');

const showLogs = ref(false);
const logsKind = ref('nate');

const openLogs = (kind) => {
  logsKind.value = kind;
  showLogs.value = true;
};
</script>
